import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  Button,
  Form,
  FormGroup,
  Input,
  InputGroup,
  Label,
  Table,
} from "reactstrap";
import { toast } from "react-toastify";
import { PaginationControl } from "react-bootstrap-pagination-control";
import { useDispatch } from "react-redux";
import { t } from "i18next";

import { JobRole, PAGE_SIZE_OPTIONS, USER_ROLE } from "../../constants";
import LocalstorageService from "../../helpers/localstorage-service";
import MachineService from "../../services/machines";

import { setLoadingOverlay } from "../../store/slices/loadingOverlaySlice";

import SortableHeader from "../../components/SortableHeader";
import NoRecords from "../../components/Table/NoRecords";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";

import useListPage from "../../hooks/useListPage";

const HeaderList = [
  {
    title: '',  // Edit button column (RIGHT side in RTL)
    value: 'editAction',
    style: { minWidth: '70px', width: '70px' },
    sortable: false,
  },
  {
    title: t("MACHINE.SERIAL_NAME_LABEL"),
    value: "terminalNumber",
    style: { minWidth: "120px", width: "15%" },
  },
  {
    title: t("LOCATION.TITLE"),
    value: "locationName",
    style: { minWidth: "150px", width: "25%" },
  },
  {
    title: t("MACHINE.TERMINAL_TYPE_LABEL"),
    value: "terminalType",
    style: { minWidth: "120px", width: "15%" },
  },
  {
    title: t("MACHINE.MODEM_TYPE_LABEL"),
    value: "modemType",
    style: { minWidth: "120px", width: "15%" },
  },
  {
    title: '',  // Delete button column (LEFT side in RTL)
    value: 'deleteAction',
    style: { minWidth: '70px', width: '70px' },
    sortable: false,
  },
];

function MachinesList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [setPageTitle, setBreadcrumbItems] = useOutletContext();

  const [roleID, setRoleID] = useState("");
  const [showDelRec, setShowDelRec] = useState(false);
  const showDelRecRef = useRef(false);

  // Activate modal (unique to Machines)
  const [activateItem, setActivateItem] = useState(null);
  const [showActivateModal, setShowActivateModal] = useState(false);

  const {
    list,
    isLoading,
    totalCount,
    currentPage,
    itemsPerPage,
    deleteModal,
    fetchList,
    debouncedSearch,
    sortBy,
    handlePageClick,
    handlePageSizeChange,
    openDeleteModal,
    closeDeleteModal,
    confirmDelete,
  } = useListPage({
    service: MachineService,
    listMethod: "listMachines",
    deleteMethod: "deleteMachine",
    initialSort: { value: 'terminalNumber', asc: true },
    initialPageSize: 10,
    deleteIdField: "terminalNumber",
    buildPayload: (payload) => {
      payload.showDelRec = showDelRecRef.current;
    },
  });

  useEffect(() => {
    setPageTitle(t("MACHINE.TITLE"));
    setBreadcrumbItems([
      { title: t("APPLICATION_TITLE"), link: "#" },
      { title: t("MACHINE.TITLE"), link: "#" },
    ]);

    const jobRole = LocalstorageService.getItem(JobRole);
    setRoleID(jobRole);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const editFn = (item) => {
    navigate(`../update/${item.terminalNumber}`, {
      state: { companyId: item.companyId },
    });
  };

  const handleShowDeleteRec = () => {
    const newShowDelRec = !showDelRec;
    setShowDelRec(newShowDelRec);
    showDelRecRef.current = newShowDelRec;
    // Reset to page 1 and re-fetch (buildPayload reads from ref)
    handlePageClick(1);
  };

  const closeActivateModal = () => {
    setActivateItem(null);
    setShowActivateModal(false);
  };

  const confirmActivate = async () => {
    dispatch(setLoadingOverlay(true));
    await MachineService.activateTerminal({
      terminalNumber: activateItem.terminalNumber,
      companyId: activateItem.companyId,
    })
      .then((response) => {
        const { status, message } = response.data;

        if (status === 400) {
          toast.error(message);
          return;
        }

        toast.success(message);
        fetchList();
      })
      .finally(() => {
        dispatch(setLoadingOverlay(false));
      });

    closeActivateModal();
  };

  return (
    <React.Fragment>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <Form className="search-filter" onSubmit={(e) => e.preventDefault()}>
          <InputGroup>
            <Input onChange={debouncedSearch} placeholder={t('SEARCH')} />
            <Button color="primary" type="button">
              <i className="ri-search-line"></i>
            </Button>
          </InputGroup>
        </Form>

        <div className="d-flex align-items-center gap-3">
          {(roleID === USER_ROLE.SUPER_ADMIN || roleID === USER_ROLE.COMPANY_ADMIN) && (
            <FormGroup className="mb-0" check>
              <Input
                id="showDeleteRec"
                title={t('SHOW_DEL_REC')}
                type="checkbox"
                onChange={handleShowDeleteRec}
                checked={showDelRec}
              />
              <Label htmlFor="showDeleteRec" className="mb-0">
                {t('SHOW_DEL_REC')}
              </Label>
            </FormGroup>
          )}
          <Button color="primary" onClick={() => navigate('../add')}>
            {t('MACHINE.ADD')}
          </Button>
        </div>
      </div>

      <Table striped bordered responsive size="sm">
        <thead>
          <tr>
            <SortableHeader
              headers={HeaderList}
              isDisabled={list.length === 0}
              sortClick={sortBy}
              showActionCol={false}
            />
          </tr>
        </thead>

        <tbody>
          {list.map((item, l0Idx) => (
            <tr key={l0Idx}>
              {/* Edit button - RIGHT side in RTL */}
              <td className="text-center">
                {showDelRec ? (
                  <Button
                    color="success"
                    size="sm"
                    title={t('ACTIVATE')}
                    onClick={() => {
                      setActivateItem(item);
                      setShowActivateModal(true);
                    }}
                  >
                    {t('ACTIVATE')}
                  </Button>
                ) : (
                  <Button color="primary" size="sm" title={t('MACHINE.UPDATE')} onClick={() => editFn(item)}>
                    {t('MACHINE.EDIT_BTN') || t('EDIT') || 'שנה'}
                  </Button>
                )}
              </td>
              <td>{item.terminalNumber}</td>
              <td>{item.locationName}</td>
              <td>{item.terminalTypeName || ''}</td>
              <td>{item.modemTypeName || ''}</td>
              {/* Delete button - LEFT side in RTL */}
              <td className="text-center">
                {!showDelRec && (
                  <Button color="danger" size="sm" title={t('MACHINE.DELETE')} onClick={() => openDeleteModal(item)}>
                    {t('MACHINE.DELETE_BTN') || 'לבטל'}
                  </Button>
                )}
              </td>
            </tr>
          ))}

          {!isLoading && list.length === 0 && (
            <NoRecords colSpan={HeaderList.length} />
          )}
        </tbody>
      </Table>

      {/* Pagination and Rows per page */}
      <div className="custom-pagination mt-2 d-flex justify-content-between align-items-center flex-row-reverse">
        {/* Rows per page - RIGHT side in RTL */}
        <div className="d-flex align-items-center gap-2">
          <Label className="mb-0">{t('ROWS_PER_PAGE') || 'שורות לדף'}:</Label>
          <Input
            type="select"
            value={itemsPerPage}
            onChange={handlePageSizeChange}
            style={{ width: '80px' }}
            bsSize="sm"
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </Input>
        </div>

        {/* Pagination - LEFT side in RTL */}
        <PaginationControl
          page={currentPage}
          total={totalCount}
          limit={itemsPerPage}
          changePage={handlePageClick}
          next={true}
          last={true}
          ellipsis={1}
        />
      </div>

      <ConfirmDeleteModal
        isOpen={showActivateModal}
        isLoading={isLoading}
        message={t("ALERT.ACT_TERMINAL")}
        modalSize=""
        confirmFn={confirmActivate}
        cancelFn={closeActivateModal}
        btnName={t('ACTIVATE')}
      />

      <ConfirmDeleteModal
        isOpen={deleteModal.show}
        isLoading={isLoading}
        confirmFn={confirmDelete}
        cancelFn={closeDeleteModal}
      />
    </React.Fragment>
  );
}

export default MachinesList;
